
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { ViewState, Role } from "../types";
import { 
    LayoutDashboard, ShoppingBag, ShieldCheck, Users, Settings, Wallet, 
    MessageSquare, FileText, Info, BookOpen, Palette, Store, BarChart3, 
    Repeat, ArrowRightLeft, Globe, Vote, FlaskConical, Cpu, ShieldAlert,
    History, Key
} from 'lucide-react';
import { TokenType } from "../types";

// --- NETWORK MINT ADDRESSES ---
export const GHOST_SUI_MINT = "0x7611862f472f10b7410eb705e7e4e1160a0827299f0312674e2d2740a8300000::ghost::GHOST";
export const VEGHOST_SUI_MINT = "0x7611862f472f10b7410eb705e7e4e1160a0827299f0312674e2d2740a8300000::ghost::veGHOST";
export const GHOST_SOL_MINT = "Ghost111111111111111111111111111111111111111"; 
export const USDC_SOL_MINT = "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyB7u6";

export const GHOST_ICON_URL = 'https://raw.githubusercontent.com/ghost-protocol-labs/ghost-token-sui/refs/heads/main/assets/GHOST.svg';

// --- ASSET HIERARCHY ---
export interface TokenConfig {
  symbol: string;
  name: string;
  type: TokenType;
  mint: string;
  decimals: number;
  chain: 'SOL' | 'SUI';
  icon: string;
  color: string;
}

export const TOKEN_CONFIGS: Record<string, TokenConfig> = {
  SUI: { 
    symbol: 'SUI', 
    name: 'Sui', 
    type: 'SUI',
    mint: '0x2::sui::SUI', 
    decimals: 9, 
    chain: 'SUI', 
    color: 'bg-blue-500',
    icon: 'https://cryptologos.cc/logos/sui-sui-logo.svg?v=031' 
  },
  GHOST_SUI: { 
    symbol: 'GHOST', 
    name: 'Ghost Token', 
    type: 'GHOST',
    mint: GHOST_SUI_MINT, 
    decimals: 9, 
    chain: 'SUI', 
    color: 'bg-stone-800',
    icon: GHOST_ICON_URL 
  },
  VEGHOST: {
    symbol: 'veGHOST',
    name: 'Voting Ghost',
    type: 'veGHOST',
    mint: VEGHOST_SUI_MINT,
    decimals: 9,
    chain: 'SUI',
    color: 'bg-nobel-gold',
    icon: GHOST_ICON_URL
  },
  SOL: { 
    symbol: 'SOL', 
    name: 'Solana', 
    type: 'SOL',
    mint: 'So11111111111111111111111111111111111111112', 
    decimals: 9, 
    chain: 'SOL', 
    color: 'bg-indigo-500',
    icon: 'https://cryptologos.cc/logos/solana-sol-logo.svg?v=031' 
  },
  USDC: { 
    symbol: 'USDC', 
    name: 'USD Coin', 
    type: 'USDC',
    mint: USDC_SOL_MINT, 
    decimals: 6, 
    chain: 'SOL', 
    color: 'bg-blue-400',
    icon: 'https://cryptologos.cc/logos/usd-coin-usdc-logo.svg?v=031' 
  },
};

// --- CORE NAVIGATION (ROLE-BASED) ---
export interface NavConfig {
  view: ViewState;
  icon: React.ElementType;
  label: string;
  roles: Role[];
  section: string;
}

export const navigationConfig: NavConfig[] = [
    { view: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['superadmin', 'admin', 'merchant', 'cashier', 'agent', 'client'], section: 'Main Application' },
    { view: 'portfolio', icon: BarChart3, label: 'Portfolio', roles: ['superadmin', 'admin', 'merchant', 'cashier', 'agent', 'client'], section: 'Main Application' },
    { view: 'wallet', icon: Wallet, label: 'Wallet Hub', roles: ['superadmin', 'admin', 'merchant', 'cashier', 'agent', 'client'], section: 'Main Application' },
    { view: 'governance', icon: Vote, label: 'DAO Governance', roles: ['superadmin', 'admin', 'merchant', 'client'], section: 'Main Application' },
    
    { view: 'swap', icon: Repeat, label: 'Swap Engine', roles: ['superadmin', 'admin', 'merchant', 'client'], section: 'Liquidity' },
    { view: 'bridge', icon: ArrowRightLeft, label: 'Cross-Chain', roles: ['superadmin', 'admin', 'merchant', 'client'], section: 'Liquidity' },
    
    { view: 'pos', icon: Store, label: 'GhostPOS', roles: ['superadmin', 'admin', 'merchant', 'cashier'], section: 'Commerce' },
    { view: 'invoice', icon: FileText, label: 'Invoices', roles: ['superadmin', 'admin', 'merchant', 'cashier'], section: 'Commerce' },
    { view: 'escrow', icon: ShieldCheck, label: 'Escrow Service', roles: ['superadmin', 'admin', 'merchant', 'agent', 'client'], section: 'Commerce' },
    
    { view: 'chat', icon: MessageSquare, label: 'AI Assistant', roles: ['superadmin', 'admin', 'merchant', 'agent'], section: 'AI & Automation' },
    { view: 'management', icon: Cpu, label: 'Infrastructure', roles: ['superadmin', 'admin', 'merchant'], section: 'System' },
    { view: 'settings', icon: Settings, label: 'Vault Settings', roles: ['superadmin', 'admin', 'merchant', 'cashier', 'agent', 'client'], section: 'System' },
    
    { view: 'docs', icon: BookOpen, label: 'Documentation', roles: ['superadmin', 'admin', 'merchant', 'cashier', 'agent', 'client'], section: 'Resources' },
    { view: 'legal', icon: FileText, label: 'Legal Center', roles: ['superadmin', 'admin', 'merchant', 'cashier', 'agent', 'client'], section: 'Resources' },
    { view: 'about', icon: Info, label: 'About Ghost', roles: ['superadmin', 'admin', 'merchant', 'cashier', 'agent', 'client'], section: 'Resources' },
];
